/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import Logger from '../logger/PerformanceMonitorLogger';
import {IMonitor, MonitorCategory} from '../types/types';

/**
 * The `BaseMonitor` class provides the base class implementation for all APM
 * Monitor implementations, providing the most elementary implementation which
 * is required by all Monitors.
 */
export default class BaseMonitor implements IMonitor {
  /**
   * Provides a reference to the `MonitorCategory` for which the monitor
   * instance is being executed, which is used for categorizing logs and
   * metrics.
   */
  protected _category: MonitorCategory;

  /**
   * Provides a reference to the context for which the monitor instance,
   * is being executed, which is used for providing additional information
   * about the monitoring environment.
   */
  protected _context: string;

  /**
   * Provides a reference to the `Logger` instance which is used for logging
   * all monitoring events.
   */
  protected _logger: Logger;

  /**
   * The `PerformanceMonitor` constructor initializes the monitor for a specific
   * category.
   *
   * @param category The category under which this monitor operates, which is
   *        used for providing categorization within the underlying metrics.
   */
  constructor(category: MonitorCategory) {
    this._logger   = new Logger(category);
    this._category = category;
  }

  /**
   * Provides a `category` getter which determines the `category` in which the
   * Monitor instance is set. A Performance Monitor instance is only ever associated
   * with a single `MonitorCategory`, and is set via the constructor during instantiation.
   *
   * ```typescript
   *  // reference the category assigned to the monitor instance ...
   *  const {category} = monitor;
   * ```
   *
   * @returns The category of the Monitor instance.
   */
  get category(): string {
    return this._category;
  }

  /**
   * Provides a `context` getter which determines the `context` in which the
   * Monitor is associated.
   *
   * ```typescript
   *  // reference the current context of the monitor instance ...
   *  const {context} = monitor;
   * ```
   *
   * @returns The current context of the Monitor is associated
   */
  get context(): string {
    return this._context;
  }

  /**
   * Provides a `context` setter which allows for assigning the `context` in
   * which the Monitor is associated.
   *
   * ```typescript
   *  // set the current context of the monitor instance ...
   *  monitor.context = 'some/page';
   * ```
   *
   * @param The current context to which the Monitor is to be associated.
   */
  set context(value: string) {
    if (value && value !== this._context) {
      this._context = value;
      if (Logger.isEnabled) {
        this._logger.context = value;
      }
    }
  }
}
